import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, ExternalLink } from "lucide-react";
import { ReviewCard } from "@/components/ReviewCard";
import { useScrollReveal } from "@/hooks/useAnimations";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";
import { AggregateRatingJsonLd } from "@/components/AggregateRatingJsonLd";

const GOOGLE_REVIEW_URL = "https://www.google.com/search?q=mediastore+niort+avis";

export const ReviewsSection = () => {
  const { data, loading } = useGoogleReviews();
  const [reviewsRef, reviewsVisible] = useScrollReveal(0.1);

  const reviews = (data.reviews || []).slice(0, 3);
  const rating = data.rating || 4.9;
  const total = data.total || 71;

  return (
    <section className="section-padding bg-white" data-testid="home-reviews-section">
      <AggregateRatingJsonLd
        rating={rating}
        total={total}
        googleMapsUrl={data.google_maps_url}
      />
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Ce que disent nos clients
            </h2>
            <div className="flex items-center gap-3 text-lg text-slate-600">
              <span className="flex items-center gap-1" aria-label={`Note ${rating} sur 5`}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={`home-rating-star-${i}`}
                    className={`h-5 w-5 ${
                      i <= Math.round(rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-slate-300"
                    }`}
                  />
                ))}
              </span>
              <span className="font-semibold text-slate-900" data-testid="home-rating-value">
                {rating.toFixed(1)}/5
              </span>
              <span className="text-slate-500" data-testid="home-rating-total">
                ({total} avis Google)
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="gap-2 scale-hover w-full sm:w-auto"
                data-testid="leave-google-review-btn-home"
              >
                Laisser un avis Google
                <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
            <Link to="/avis-clients">
              <Button
                variant="outline"
                className="gap-2 scale-hover w-full sm:w-auto"
                data-testid="view-all-reviews-btn"
              >
                Voir tous les avis
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        <div
          ref={reviewsRef}
          className={`grid md:grid-cols-3 gap-6 transition-all duration-1000 ${
            reviewsVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          {loading || reviews.length === 0 ? (
            <>
              <div className="h-48 skeleton rounded-xl" data-testid="reviews-skeleton-1" />
              <div className="h-48 skeleton rounded-xl" data-testid="reviews-skeleton-2" />
              <div className="h-48 skeleton rounded-xl" data-testid="reviews-skeleton-3" />
            </>
          ) : (
            reviews.map((review, index) => (
              <div
                key={`${review.author}-${index}`}
                className="card-lift"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <ReviewCard
                  review={{
                    id: `${review.author}-${index}`,
                    author_name: review.author,
                    rating: review.rating,
                    text: review.text,
                    relative_time: review.relative_publish_time,
                    profile_photo_url: review.profile_photo_url,
                  }}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
